import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, AlertTriangle, EyeOff } from "lucide-react";
import { StageStagnationConfig, StageVisibilityConfig } from "@/types/pipeline";

interface StageFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (stage: {
    name: string;
    description?: string;
    color: string;
    level: number;
    orden: number;
    stagnation_config?: StageStagnationConfig | null;
    visibility_config?: StageVisibilityConfig | null;
  }) => void;
  initialStage?: {
    name: string;
    description?: string;
    color: string;
    level: number;
    orden?: number;
    stagnation_config?: StageStagnationConfig | null;
    visibility_config?: StageVisibilityConfig | null;
  };
  isEdit?: boolean;
  defaultLevel?: number;
  defaultOrden?: number;
  maxOrden?: number;
}

const LEVEL_GROUPS = [
  { label: "Inicial", level: 0 },
  { label: "En progreso", level: 1 },
  { label: "Finalizada", level: 2 },
];

export function StageFormModal({
  isOpen,
  onClose,
  onSave,
  initialStage,
  isEdit = false,
  defaultLevel = 0,
  defaultOrden = 1,
  maxOrden = 10,
}: StageFormModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#6366f1");
  const [level, setLevel] = useState(defaultLevel);
  const [orden, setOrden] = useState(defaultOrden);
  const [stagnationDays, setStagnationDays] = useState<string>("");
  const [stagnationAction, setStagnationAction] = useState<
    "notify" | "move_to_lost"
  >("notify");
  const [visibilityValue, setVisibilityValue] = useState<string>("");
  const [visibilityUnit, setVisibilityUnit] = useState<string>("hours");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const ordenOptions = Array.from({ length: maxOrden }, (_, i) => i + 1);

  useEffect(() => {
    if (isOpen) {
      if (initialStage) {
        setName(initialStage.name);
        setDescription(initialStage.description || "");
        setColor(initialStage.color || "#6366f1");
        setLevel(initialStage.level ?? defaultLevel);
        setOrden(initialStage.orden ?? defaultOrden);
        if (initialStage.stagnation_config) {
          setStagnationDays(initialStage.stagnation_config.value.toString());
          setStagnationAction(initialStage.stagnation_config.action as any);
        } else {
          setStagnationDays("");
        }
        if (initialStage.visibility_config) {
          setVisibilityValue(initialStage.visibility_config.value.toString());
          setVisibilityUnit(initialStage.visibility_config.unit);
        } else {
          setVisibilityValue("");
        }
      } else {
        setName("");
        setDescription("");
        setColor("#6366f1");
        setLevel(defaultLevel);
        setOrden(defaultOrden);
        setStagnationDays("");
        setVisibilityValue("");
      }
      setErrors({});
    }
  }, [isOpen, initialStage, defaultLevel, defaultOrden]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "El nombre de la etapa es obligatorio";
    }

    if (orden < 1) {
      newErrors.orden = "El orden debe ser mayor a 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSave({
      name: name.trim(),
      description: description.trim(),
      color,
      level,
      orden,
      stagnation_config:
        stagnationDays && level === 1
          ? {
              value: parseInt(stagnationDays),
              unit: "days",
              action: stagnationAction,
            }
          : null,
      visibility_config:
        visibilityValue && level === 2
          ? {
              value: parseInt(visibilityValue),
              unit: visibilityUnit as any,
            }
          : null,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Etapa" : "Nueva Etapa"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Modifica los detalles de la etapa."
              : "Define los detalles para la nueva etapa del pipeline."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="stage-name">Nombre de la etapa *</Label>
            <Input
              id="stage-name"
              placeholder="Ej: Prospecto, Cotizaciones, Negociación..."
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.name;
                    return newErrors;
                  });
                }
              }}
              className={errors.name ? "border-red-500" : ""}
              required
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {isEdit && (
            <div>
              <Label htmlFor="stage-orden">Orden de la etapa *</Label>
              <p className="text-xs text-slate-500 mb-1">
                Define la posición de esta etapa en el pipeline (1 = primera,{" "}
                {maxOrden} = última)
              </p>
              <Select
                value={orden.toString()}
                onValueChange={(value) => setOrden(parseInt(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un orden" />
                </SelectTrigger>
                <SelectContent>
                  {ordenOptions.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      Posición {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.orden && (
                <p className="text-red-500 text-sm mt-1">{errors.orden}</p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="stage-description">Descripción</Label>
            <Textarea
              id="stage-description"
              placeholder="Ej: Etapa donde se evalúa la cotización inicial..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="stage-color">Color de la etapa</Label>
            <div className="flex items-center gap-3 mt-2">
              <Input
                id="stage-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-20 h-10 cursor-pointer"
              />
              <span className="text-sm text-slate-600">{color}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                "#6366f1",
                "#2563eb",
                "#0ea5e9",
                "#14b8a6",
                "#22c55e",
                "#84cc16",
                "#eab308",
                "#f59e42",
                "#f43f5e",
                "#ec4899",
                "#a21caf",
                "#8b5cf6",
                "#78716c",
                "#64748b",
                "#374151",
              ].map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-8 w-8 rounded-md border-2 flex-shrink-0 transition-all hover:scale-110 ${
                    color === c ? "border-black scale-110" : "border-slate-300"
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  title={c}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>Grupo de la etapa</Label>
            <p className="text-xs text-slate-500 mb-3">
              El grupo determina el estado general de las cotizaciones en esta
              etapa
            </p>
            <div className="grid grid-cols-3 gap-2">
              {LEVEL_GROUPS.map((group) => (
                <Button
                  key={group.level}
                  type="button"
                  size="sm"
                  variant={level === group.level ? "default" : "outline"}
                  onClick={() => setLevel(group.level)}
                  className={`justify-start ${
                    level === group.level
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "hover:bg-slate-100"
                  }`}
                >
                  {group.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Control de Estancamiento (Solo para etapas "En Progreso") */}
          {level === 1 && (
            <div className="pt-4 border-t border-slate-100">
              <Label className="flex items-center gap-2 mb-2 text-slate-700">
                <Clock className="h-4 w-4" />
                Control de Estancamiento
              </Label>
              <div className="bg-slate-50 p-3 rounded-md border border-slate-200 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label
                      htmlFor="stagnation-days"
                      className="text-xs text-slate-500 mb-1 block"
                    >
                      Días máx. en etapa
                    </Label>
                    <Input
                      id="stagnation-days"
                      type="number"
                      min="0"
                      placeholder="Ej: 30"
                      value={stagnationDays}
                      onChange={(e) => setStagnationDays(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="stagnation-action"
                      className="text-xs text-slate-500 mb-1 block"
                    >
                      Acción al exceder
                    </Label>
                    <Select
                      value={stagnationAction}
                      onValueChange={(v) => setStagnationAction(v as any)}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="notify">Solo Notificar</SelectItem>
                        <SelectItem value="move_to_lost">
                          Mover a Perdida
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 mt-0.5" />
                  Si una cotización excede los días configurados, el sistema
                  ejecutará la acción automáticamente.
                </p>
              </div>
            </div>
          )}

          {/* Control de Visibilidad (Solo para etapas "Finalizadas") */}
          {level === 2 && (
            <div className="pt-4 border-t border-slate-100">
              <Label className="flex items-center gap-2 mb-2 text-slate-700">
                <EyeOff className="h-4 w-4" />
                Control de Visibilidad
              </Label>
              <div className="bg-slate-50 p-3 rounded-md border border-slate-200 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label
                      htmlFor="visibility-value"
                      className="text-xs text-slate-500 mb-1 block"
                    >
                      Tiempo visible (en horas)
                    </Label>
                    <Input
                      id="visibility-value"
                      type="number"
                      min="0"
                      placeholder="Ej: 24"
                      value={visibilityValue}
                      onChange={(e) => setVisibilityValue(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 mt-0.5" />
                  Las cotizaciones en esta etapa se ocultarán de la vista
                  principal después de este tiempo. Dejar en blanco para
                  mantenerlas visibles.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim()}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
          >
            {isEdit ? "Guardar cambios" : "Crear Etapa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
